import React, {FC} from "react"
import styled, {css} from "styled-components"
import {H1, H5, Label} from "@irati/ui/src/Typography"
import {RouterLink} from "@irati/ui/src/Link"
import Flex from "@irati/ui/src/Flex"
import {useTheme} from "@irati/ui/src/global/useTheme"
import FormattedMessage from "@irati/utils/lang"

interface ProductCardProps {
  name: string
  image: string
  price: number
  url: string
  isCarousel?: boolean
}

export const ProductCard: FC<ProductCardProps> = ({name, image, price, url, isCarousel = false}) => {
  const productPath = url.split("/products")[1]

  const theme = useTheme()

  return (
    <RouterLink to={`/products${productPath}`} styled={false}>
      <Flex alignItems="center">
        <InnerContainer isCarousel={isCarousel}>
          <CardImgContainer isCarousel={isCarousel}>
            <CardImg isCarousel={isCarousel} src={image} alt="product image" />
          </CardImgContainer>

          <CardContent>
            <TitleContainer>
              <Title>{name}</Title>
            </TitleContainer>
            <PriceContainer>
              <Price>£{price.toFixed(2)}</Price>
              <LabelContainer>
                <Label>
                  <FormattedMessage id="platform.payrollpay.store.landing.c2w.popularBikes.perMonth" />
                </Label>
                <AfterSavings color={theme.colors.greys.mediumDark}>
                  <FormattedMessage id="platform.payrollpay.store.landing.c2w.popularBikes.afterSavings" />
                </AfterSavings>
              </LabelContainer>
            </PriceContainer>
          </CardContent>
        </InnerContainer>
      </Flex>
    </RouterLink>
  )
}

const PriceContainer = styled(Flex)`
  flex-direction: column;
  ${({theme}) => css`
    ${theme.mediaQueries.minWidthMedia.phoneLandscape`
      flex-direction: row;
    `}
  `}
`

const LabelContainer = styled(Flex)`
  ${({theme}) => css`
    ${theme.mediaQueries.minWidthMedia.phoneLandscape`
      flex-direction: column;
      margin-left: ${theme.spacing.one};
    `}
  `}
`

const AfterSavings = styled(Label)`
  ${({theme}) => css`
    margin-left: ${theme.spacing.one};
    ${theme.mediaQueries.minWidthMedia.phoneLandscape`
      margin-left: 0;
      margin-top: -${theme.spacing.one};
    `}
  `}
`

const InnerContainer = styled.div<{isCarousel?: boolean}>`
  ${({theme, isCarousel}) => css`
    position: relative;
    width: ${isCarousel ? "224px" : "44vw"};
    max-width: 224px;
    height: ${isCarousel ? "331px" : "301px"};
    border-radius: ${theme.border.radius.large};
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    align-items: center;
    background-color: ${theme.colors.greys.white};

    ${!isCarousel &&
    css`
      ${theme.mediaQueries.minWidthMedia.phoneLandscape`
        width: 35vw;
      `}
      ${theme.mediaQueries.minWidthMedia.tablet`
        width: 30vw;
        height: 331px;
      `}
      ${theme.mediaQueries.minWidthMedia.desktop`
        width: 20vw;
        max-width: 213px;
      `}
    `}
  `}
`

const CardImgContainer = styled.div<{isCarousel?: boolean}>`
  ${({theme, isCarousel}) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${isCarousel ? "175px" : "128px"};
    margin-top: ${theme.spacing.oneAndHalf};
    margin-bottom: ${theme.spacing.oneAndHalf};

    ${!isCarousel &&
    css`
      ${theme.mediaQueries.minWidthMedia.tablet`
        height: 175px;
      `}
    `}
  `}
`

const CardImg = styled.img<{isCarousel?: boolean}>`
  object-fit: fill;
  height: ${({isCarousel}) => (isCarousel ? "175px" : "128px")};

  ${({isCarousel, theme}) =>
    !isCarousel &&
    css`
      ${theme.mediaQueries.minWidthMedia.tablet`
      height: 175px;
    `}
    `}
`

const CardContent = styled.div`
  ${({theme}) => css`
    padding: ${theme.spacing.one};
    padding-top: 0;
    align-self: center;
    height: 100%;
    max-width: 200px;

    ${theme.mediaQueries.minWidthMedia.phoneLandscape`
    padding: ${theme.spacing.oneAndHalf};
    `}
  `}
`

const Price = styled(H1).attrs({as: "span"})`
  font-size: 24px;
`

const TitleContainer = styled.div`
  ${({theme}) => css`
    height: 60px;
    margin-bottom: ${theme.spacing.two};
  `}
`

const Title = styled(H5)`
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
`
